import { useState } from 'react'
import { download, query } from '../../api/client'
import { useQuery } from '../../api/hooks'
import type { Department, GaAnalysis, ReportBasis } from '../../api/types'
import { useAuth } from '../../auth/AuthContext'
import { P } from '../../auth/permissions'
import { Empty, ErrorBanner, Loading, Money, Panel, useDateRange } from '../../components/ui'

/**
 * The G&A analysis book (BUILD_PLAN §1 row 12, §6 step 3/2) — expenses broken down for analysis,
 * separate from the single P&L line, with the equivalent prior period alongside.
 *
 * This is the one report that reads the department dimension (BUILD_PLAN §3.5), which is why the
 * dimension became assignable and readable in the same piece of work: a cost centre nothing reports
 * on is a field the office fills in for months before finding out nothing was looking at it.
 */
export function GaAnalysisPage() {
  const { can } = useAuth()
  const { from, to, setFrom, setTo } = useDateRange()
  const [basis, setBasis] = useState<ReportBasis>('Accrual')

  /** '' every department · 'unassigned' what reached none · otherwise a department id. */
  const [department, setDepartment] = useState('')

  // The closed ones too. A cost centre shut halfway through the year still has spending in the
  // months it was open, and a report that could not be run for it would make closing one destroy
  // the ability to look back at it.
  const departments = useQuery<Department[]>(`/departments${query({ includeInactive: true })}`)

  const report = useQuery<GaAnalysis>(
    `/reports/ga-analysis${query({ from, to, basis, department })}`,
    [from, to, basis, department],
  )

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="field">
          <label>From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="field">
          <label>To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="field">
          <label>Basis</label>
          <select value={basis} onChange={(e) => setBasis(e.target.value as ReportBasis)}>
            <option value="Accrual">Accrual</option>
            <option value="Cash">Cash</option>
          </select>
        </div>

        {departments.data && departments.data.length > 0 && (
          <div className="field">
            <label>Cost centre</label>
            <select value={department} onChange={(e) => setDepartment(e.target.value)}>
              <option value="">All departments</option>
              {/* The control half of the dimension. Comparing cost centres without being able to
                  ask what reached none of them means comparing shares of an unknown total. */}
              <option value="unassigned">Not coded to one</option>
              {departments.data.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.code} · {d.nameEn}
                  {d.isActive ? '' : ' (closed)'}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="spacer" />

        {can(P.reports.export) && (
          <button
            onClick={() =>
              void download(
                `/reports/ga-analysis/excel${query({ from, to, basis, department })}`,
                'ga-analysis.xlsx',
              )
            }
          >
            Export to Excel
          </button>
        )}
      </div>

      <ErrorBanner error={report.error} />
      {report.loading && <Loading />}

      {report.data?.departmentLabel && (
        <div className="banner info">
          <div>
            Showing <strong>{report.data.departmentLabel}</strong> only. The percentages below are
            shares of this cost centre, not of the office — and the prior period is the same cost
            centre, so the comparison holds.
          </div>
        </div>
      )}

      {report.data && (
        <Panel flush>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Code</th>
                  <th>Expense</th>
                  <th style={{ width: 180 }}>العربية</th>
                  <th style={{ width: 130 }} className="num">
                    Amount
                  </th>
                  <th style={{ width: 90 }} className="num">
                    % of total
                  </th>
                  <th style={{ width: 130 }} className="num">
                    Prior period
                  </th>
                  <th style={{ width: 110 }} className="num">
                    Movement
                  </th>
                </tr>
              </thead>
              <tbody>
                {report.data.rows.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <Empty>
                        {report.data.departmentLabel
                          ? 'No G&A spending coded to this in the period.'
                          : 'No G&A spending in this period.'}
                      </Empty>
                    </td>
                  </tr>
                )}

                {report.data.rows.map((row) => (
                  <tr key={row.code}>
                    <td className="code">{row.code}</td>
                    <td>{row.nameEn}</td>
                    <td dir="rtl" className="soft">
                      {row.nameAr}
                    </td>
                    <td className="num">
                      <Money value={row.amount} />
                    </td>
                    <td className="num soft">{row.percentOfTotal.toFixed(1)}%</td>
                    <td className="num soft">
                      <Money value={row.priorPeriodAmount} blankZero />
                    </td>
                    <td className={row.movement > 0 ? 'num negative' : 'num positive'}>
                      <Money value={row.movement} blankZero />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}>Total general &amp; administrative</td>
                  <td className="num">
                    <Money value={report.data.total} />
                  </td>
                  <td />
                  <td className="num">
                    <Money value={report.data.priorTotal} />
                  </td>
                  <td className="num">
                    <Money value={report.data.total - report.data.priorTotal} />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Panel>
      )}
    </>
  )
}
